import React, { useEffect, useState } from 'react';

import { Icon } from '../Icon';
import { Button } from '../Button/Button';

import type {
  Column,
  ColumnOrderState,
  ColumnVisibilityState,
} from '../Table/hooks/useTable';
import { getMergedColumnOrder } from '../Table/utils/getMergedColumnOrder';
import { useTranslation } from 'react-i18next';

import styles from './ColumnVisibility.scss';
import { SidePanel } from '../SidePanel/SidePanel';
import { Checkbox } from '../Checkbox/Checkbox';
import { ICON_SET_FONTAWESOME } from 'lane-shared/helpers/constants/icons';
import { DraggableList } from './DraggableList';

type ColumnVisibilityProps<TDataShape> = {
  columns: Column<TDataShape>[];
  columnVisibility: ColumnVisibilityState;
  setColumnVisibility: (columnVisibility: ColumnVisibilityState) => void;
  columnOrder?: ColumnOrderState;
  setColumnOrder?: (columnOrder: ColumnOrderState) => void;
  isCustomFieldEnabled?: boolean;
};

export const ColumnVisibility = <TDataShape,>({
  columns,
  columnVisibility,
  setColumnVisibility,
  columnOrder = [],
  setColumnOrder = () => {},
  isCustomFieldEnabled = false,
}: ColumnVisibilityProps<TDataShape>) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [tempColumnVisibility, setTempColumnVisibility] =
    useState(columnVisibility);
  const [tempColumnOrder, setTempColumnOrder] = useState(columnOrder || []);

  type CsvColumnLike = {
    csvHeader?: unknown;
    header?: unknown;
  };

  function getCsvColumnHeader(column: CsvColumnLike): string {
    if (typeof column.csvHeader === 'string') {
      return column.csvHeader;
    }

    if (typeof column.header === 'string') {
      return column.header;
    }

    return '';
  }

  const columnVisibilityJSONStr = JSON.stringify(columnVisibility);
  const columnOrderJSONStr = JSON.stringify(columnOrder);
  const columnKeysJSONStr = JSON.stringify(columns.map(column => column.key));

  useEffect(() => {
    setTempColumnVisibility(columnVisibility);
  }, [columnVisibilityJSONStr]);

  useEffect(() => {
    setTempColumnOrder(
      isCustomFieldEnabled
        ? getMergedColumnOrder(columnOrder || [], columns)
        : columnOrder || []
    );
  }, [columnOrderJSONStr, columnKeysJSONStr, isCustomFieldEnabled]);

  const resetToDefault = () => {
    const defaultVisibility = columns.reduce((acc, column) => {
      acc[column.key] = !column.customFieldSection;

      return acc;
    }, {} as ColumnVisibilityState);

    setTempColumnVisibility(defaultVisibility);
    setTempColumnOrder(columns.map(col => col.key));
  };

  const hasChanges =
    JSON.stringify(tempColumnVisibility) !== JSON.stringify(columnVisibility) ||
    JSON.stringify(tempColumnOrder) !== JSON.stringify(columnOrder);

  const getSectionName = (column: Column<TDataShape>) =>
    column.customFieldSection || t('web.table.editColumns.section.default');

  return (
    <>
      <div className={styles.ButtonWrapper}>
        <Button
          variant="text"
          size="large"
          startIcon={<Icon name="cog" />}
          onClick={() => setIsOpen(true)}
        >
          {t('web.table.editColumns')}
        </Button>
      </div>
      <SidePanel
        header={t('web.table.editColumns')}
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        closeOnClickOutside
        headerActions={
          <Button variant="secondary" size="large" onClick={resetToDefault}>
            {t('web.table.editColumns.resetToDefault')}
          </Button>
        }
        footerActions={
          <>
            <Button
              size="large"
              onClick={() => {
                setColumnVisibility(tempColumnVisibility);
                setColumnOrder(tempColumnOrder);
                setIsOpen(false);
              }}
              disabled={!hasChanges}
            >
              {t('web.table.editColumns.save')}
            </Button>
            <Button
              size="large"
              variant="secondary"
              onClick={() => {
                setTempColumnVisibility(columnVisibility);
                setTempColumnOrder(columnOrder);
                setIsOpen(false);
              }}
            >
              {t('web.table.editColumns.cancel')}
            </Button>
          </>
        }
      >
        {(() => {
          const orderedColumns = getMergedColumnOrder(tempColumnOrder, columns)
            .map(key => columns.find(col => col.key === key))
            .filter((col): col is Column<TDataShape> => col !== undefined);

          const sectionNames = [...new Set(orderedColumns.map(getSectionName))];

          const columnsBySection = new Map<string, Column<TDataShape>[]>();

          for (const col of orderedColumns) {
            const section = getSectionName(col);

            if (!columnsBySection.has(section))
              columnsBySection.set(section, []);

            columnsBySection.get(section)!.push(col);
          }

          const handleSectionOrderChange = (
            sectionName: string,
            newSectionOrder: Column<TDataShape>[]
          ) => {
            const newOrder = sectionNames.flatMap(name =>
              name === sectionName
                ? newSectionOrder.map(column => column.key)
                : (columnsBySection.get(name) ?? []).map(column => column.key)
            );

            setTempColumnOrder(newOrder);
          };

          return sectionNames.map(sectionName => {
            const isCustomFieldSection =
              isCustomFieldEnabled &&
              !!columnsBySection.get(sectionName)?.[0]?.customFieldSection;

            const draggableList = (
              <DraggableList
                key={sectionName}
                items={columnsBySection.get(sectionName) ?? []}
                onOrderChange={newOrder =>
                  handleSectionOrderChange(sectionName, newOrder)
                }
                renderItem={(column, dragHandleProps) => (
                  <div className={styles.ColumnContainer} {...dragHandleProps}>
                    <Checkbox
                      text={getCsvColumnHeader(column)}
                      value={column.key}
                      selected={tempColumnVisibility[column.key] !== false}
                      onChange={() => {
                        setTempColumnVisibility({
                          ...tempColumnVisibility,
                          [column.key]:
                            tempColumnVisibility[column.key] === false,
                        });
                      }}
                      disabled={column.disableVisibilityToggle}
                    />
                    <Icon
                      name="bars"
                      set={ICON_SET_FONTAWESOME}
                      className={styles.iconMenu}
                    />
                  </div>
                )}
                itemKey={col => col.key}
                testId={`draggable-list-${sectionName}`}
              />
            );

            if (!isCustomFieldSection) return draggableList;

            return (
              <div key={sectionName}>
                <div className={styles.CustomFieldSectionHeader}>
                  {sectionName}
                </div>
                {draggableList}
              </div>
            );
          });
        })()}
      </SidePanel>
    </>
  );
};
